// packages/ui/src/components/settings/AutomationSettings.tsx
// Cron job management and rate limit display

export interface Automation {
  id: string
  name: string
  schedule: string
  enabled: boolean
}

export interface RateLimit {
  current: number
  max: number
  windowMs: number
}

interface AutomationSettingsProps {
  automations: Automation[]
  rateLimit: RateLimit
  onUpdate: (type: 'automation', payload: { id: string; enabled: boolean }) => void
}

export function AutomationSettings({ automations, rateLimit, onUpdate }: AutomationSettingsProps) {
  return (
    <div data-testid="settings-automation" className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-200 mb-3">Automation</h2>
        <p className="text-sm text-gray-400 mb-4">
          Manage scheduled jobs and view rate limit status.
        </p>
      </div>

      {/* Cron Jobs */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-gray-300 mb-2">Scheduled Jobs</h3>
        {automations.map((cron) => (
          <div
            key={cron.id}
            className="flex items-center justify-between p-3 rounded-lg bg-gray-800/50"
          >
            <div className="flex-1 min-w-0">
              <span
                data-testid={`cron-name-${cron.id}`}
                className="text-sm text-gray-200 block"
              >
                {cron.name}
              </span>
              <code
                data-testid={`cron-schedule-${cron.id}`}
                className="text-xs font-mono text-gray-500"
              >
                {cron.schedule}
              </code>
            </div>
            <input
              type="checkbox"
              data-testid={`cron-toggle-${cron.id}`}
              checked={cron.enabled}
              onChange={() =>
                onUpdate('automation', { id: cron.id, enabled: !cron.enabled })
              }
              className="w-4 h-4 rounded border-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-900"
            />
          </div>
        ))}
        {automations.length === 0 && (
          <p className="text-sm text-gray-500">No scheduled jobs configured.</p>
        )}
      </div>

      {/* Rate Limit */}
      <div>
        <h3 className="text-sm font-medium text-gray-300 mb-2">Rate Limits</h3>
        <div
          data-testid="rate-limit-display"
          className="p-3 rounded-lg bg-gray-800/50"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Current usage</span>
            <span className="text-sm text-gray-200">
              {rateLimit.current} / {rateLimit.max}
            </span>
          </div>
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                rateLimit.current / rateLimit.max > 0.8
                  ? 'bg-red-500'
                  : 'bg-blue-500'
              }`}
              style={{ width: `${Math.min((rateLimit.current / rateLimit.max) * 100, 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Window: {rateLimit.windowMs / 1000}s
          </p>
        </div>
      </div>
    </div>
  )
}
